/**
 * Пакет @hive/i18n — тексты и форматтеры УЛЬЯ.
 *
 * Назначение файла: собрать экземпляр i18next с поддержкой ICU и отдать его клиенту
 * и серверу. Один каталог на обе стороны — обязательное требование: пуши локализует
 * сервер по player.locale, и второй набор текстов немедленно разъехался бы с первым
 * (docs/19-localization.md §19.4, docs/adr/0005-i18n-foundation.md).
 *
 * Импорты:
 *   i18next     — сам движок переводов
 *   ./icu       — свой плагин ICU MessageFormat: русские plural-формы one/few/many
 *   ./locales   — реестр 20 локалей, фолбэки, namespaces
 *   ./resources — встроенные каталоги переводов
 *
 * Использование на клиенте:
 *   const i18n = await createI18n("ru");
 *   i18n.t("hex:card.hearths", { count: 3 });
 *
 * Использование на сервере (пуш конкретному игроку):
 *   const t = await translatorFor(player.locale);
 *   t("push:siege.declared.body", { attacker, hex, time });
 */

import i18next, { type i18n as I18nInstance, type TFunction } from "i18next";

import { IcuFormat } from "./icu.js";

import {
  FALLBACK_LOCALE,
  NAMESPACES,
  SOURCE_LOCALE,
  type LocaleCode,
  type Namespace,
} from "./locales.js";
import { localeResources } from "./resources.js";

export * from "./locales.js";
export * from "./format.js";
export { IcuFormat, type IcuFormatOptions } from "./icu.js";
export { type Catalog, type LocaleResources, RESOURCES, localeResources } from "./resources.js";

/** Namespace по умолчанию: тексты, которые нужны на любом экране. */
const DEFAULT_NAMESPACE: Namespace = "common";

/** Собирает ресурсы в формате, который ждёт i18next: { локаль: { namespace: каталог } }. */
function buildResources(locales: ReadonlyArray<LocaleCode>) {
  const resources: Record<string, Record<string, Record<string, string>>> = {};
  for (const locale of locales) {
    const catalogs = localeResources(locale);
    const bundle: Record<string, Record<string, string>> = {};
    for (const namespace of NAMESPACES) {
      const catalog = catalogs[namespace];
      if (catalog) bundle[namespace] = { ...catalog };
    }
    resources[locale] = bundle;
  }
  return resources;
}

/**
 * Создаёт независимый экземпляр i18next.
 *
 * Независимость важна для сервера: он обслуживает игроков с разными локалями
 * одновременно, и общий изменяемый экземпляр приводил бы к пушам на чужом языке.
 *
 * @param locale язык интерфейса
 * @param options.debug выводить в консоль отсутствующие ключи (только для разработки)
 */
export async function createI18n(
  locale: LocaleCode,
  options: { debug?: boolean } = {},
): Promise<I18nInstance> {
  const instance = i18next.createInstance();

  await instance
    // Свой плагин ICU: разбирает plural-формы и подстановки (см. ./icu.ts)
    .use(new IcuFormat({ memoize: true }))
    .init({
      lng: locale,
      // Русский — источник текстов, английский — второй фолбэк на случай неготового перевода
      fallbackLng: [SOURCE_LOCALE, FALLBACK_LOCALE],
      supportedLngs: [...new Set([locale, SOURCE_LOCALE, FALLBACK_LOCALE])],
      ns: [...NAMESPACES],
      defaultNS: DEFAULT_NAMESPACE,
      resources: buildResources([...new Set([locale, SOURCE_LOCALE, FALLBACK_LOCALE])]),
      // Ключи плоские с точками: "card.owner.you" — это один ключ, а не вложенность
      keySeparator: false,
      nsSeparator: ":",
      interpolation: {
        // ICU сам разбирает {параметры}; интерполяция i18next не нужна и мешает
        escapeValue: false,
      },
      returnNull: false,
      debug: options.debug ?? false,
    });

  return instance;
}

/**
 * Готовая функция перевода для одной локали — то, что нужно серверу для пуша или письма.
 * Экземпляры кэшируются по локали: создание i18next стоит дорого, а пуши отправляются часто.
 */
const translatorCache = new Map<LocaleCode, Promise<TFunction>>();

export function translatorFor(locale: LocaleCode): Promise<TFunction> {
  let cached = translatorCache.get(locale);
  if (!cached) {
    cached = createI18n(locale).then((instance) => instance.t.bind(instance) as TFunction);
    translatorCache.set(locale, cached);
  }
  return cached;
}

/**
 * Сбрасывает кэш переводчиков. Нужен тестам и горячей перезагрузке текстов;
 * в бою вызывать не требуется.
 */
export function resetTranslatorCache(): void {
  translatorCache.clear();
}
